import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Plus, Download, Trash2, FileDown } from "lucide-react";

const emptyItem = { description: "", quantity: 1, unit_price: 0, total: 0 };
const emptyForm = { associate_id: "", items: [{ ...emptyItem }], tax_rate: 21, due_date: "", notes: "", recurring: false, recurring_interval: null };

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => setItems((await api.get("/invoices")).data);
  useEffect(() => { load(); api.get("/associates").then(({ data }) => setAssociates(data)); }, []);

  const updateItem = (idx, patch) => {
    const it = [...form.items];
    it[idx] = { ...it[idx], ...patch };
    it[idx].total = (it[idx].quantity || 0) * (it[idx].unit_price || 0);
    setForm({ ...form, items: it });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });

  const create = async () => {
    if (!form.associate_id || !form.due_date) return toast.error("Cal associat i venciment");
    try {
      await api.post("/invoices", {
        ...form,
        items: form.items.map(i => ({ ...i, quantity: parseFloat(i.quantity), unit_price: parseFloat(i.unit_price), total: parseFloat(i.quantity) * parseFloat(i.unit_price) })),
      });
      toast.success("Creada i enviada per correu");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const markPaid = async (id) => {
    await api.put(`/invoices/${id}/status`, { status: "paid", payment_status: "paid" });
    toast.success("Marcat com pagat"); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Segur?")) return;
    await api.delete(`/invoices/${id}`); load();
  };
  const exportCsv = async () => {
    const token = localStorage.getItem("ss_token");
    const res = await fetch(`${API_BASE}/exports/invoices.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "factures.csv"; a.click();
  };
  const downloadPdf = async (inv) => {
    const token = localStorage.getItem("ss_token");
    const res = await fetch(`${API_BASE}/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${inv.number}.pdf`; a.click();
  };

  const assocName = (id) => associates.find(a => a.id === id)?.commercial_name || "—";

  return (
    <div className="space-y-6" data-testid="admin-invoices">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline text-accent">Facturació</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Factures</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={exportCsv} data-testid="invoices-export-btn"><Download className="w-4 h-4 mr-2" /> CSV</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full" data-testid="invoice-new-btn"><Plus className="w-4 h-4 mr-2" /> Nova</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Nova factura</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Associat</Label>
                    <Select value={form.associate_id} onValueChange={(v) => setForm({ ...form, associate_id: v })}>
                      <SelectTrigger data-testid="invoice-form-associate"><SelectValue placeholder="Escull..." /></SelectTrigger>
                      <SelectContent>{associates.map(a => <SelectItem key={a.id} value={a.id}>{a.commercial_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Venciment</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                </div>
                <div>
                  <Label>Línies</Label>
                  <div className="space-y-2 mt-2">
                    {form.items.map((it, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <Input className="col-span-6" placeholder="Descripció" value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} />
                        <Input className="col-span-2" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} />
                        <Input className="col-span-3" type="number" step="0.01" placeholder="Preu" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })} />
                        <div className="col-span-1 text-sm text-right">{(it.quantity * it.unit_price || 0).toFixed(2)}</div>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={addItem}>+ Línia</Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>IVA (%)</Label><Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) })} /></div>
                  <div className="flex items-center gap-2"><Switch checked={form.recurring} onCheckedChange={(v) => setForm({ ...form, recurring: v })} /><Label>Recurrent</Label></div>
                </div>
                {form.recurring && (
                  <Select value={form.recurring_interval || "monthly"} onValueChange={(v) => setForm({ ...form, recurring_interval: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="monthly">Mensual</SelectItem><SelectItem value="quarterly">Trimestral</SelectItem><SelectItem value="yearly">Anual</SelectItem></SelectContent>
                  </Select>
                )}
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="invoice-form-save">Emetre</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="rounded-2xl border-2 overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Núm.</TableHead><TableHead>Associat</TableHead><TableHead>Import</TableHead><TableHead>Estat</TableHead><TableHead>Emissió</TableHead><TableHead>Venciment</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {items.map((inv) => (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.number}`}>
                  <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                  <TableCell>{assocName(inv.associate_id)}</TableCell>
                  <TableCell className="font-semibold">{inv.total.toFixed(2)} €</TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded-full ${inv.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{inv.payment_status}</span></TableCell>
                  <TableCell className="text-xs">{inv.issue_date}</TableCell>
                  <TableCell className="text-xs">{inv.due_date}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadPdf(inv)} data-testid={`invoice-pdf-${inv.number}`}><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>
                      {inv.payment_status !== "paid" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => markPaid(inv.id)} data-testid={`invoice-paid-${inv.number}`}>Marca pagat</Button>}
                      <Button size="icon" variant="ghost" onClick={() => remove(inv.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sense factures.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
